import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money, ProductType } from "./Basket";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

const longDate = (iso: string) => {
	const d = new Date(iso);
	return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "long", weekday: "long" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

const clock = (iso: string) =>
	new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/**
 * Hospitality for one fixture.
 *
 * A list of packages, not a quantity picker. Somebody buying a lounge is
 * choosing between two or three things and wants to read what's in them —
 * which is why this is its own page rather than a fourth ticket type buried
 * under general admission.
 */
export function ShopHospitality() {
	const { slug = "", id = "" } = useParams();
	const navigate = useNavigate();
	const [data, setData] = React.useState<any>(null);
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.getHospitality(slug, Number(id));
				if (!res.ok) {
					if (live) setError("We can't find that.");
					return;
				}
				if (live) setData(await res.json());
			} catch {
				if (live) setError("We couldn't load that. Please try again.");
			}
		})();
		return () => { live = false; };
	}, [slug, id]);

	if (error || (data && !data.packages?.length)) {
		return (
			<div className="shop has-nav" style={clubTheme(data?.club)}>
				<ShopHeader club={data?.club} slug={slug} />
				<div className="shop-result">
					<h2>No hospitality for this one</h2>
					<p>{error || "There aren't any packages on sale for this game."}</p>
					<button className="shop-btn" onClick={() => navigate(`/club/${slug}/fixture/${id}`)} type="button">
						See match tickets
					</button>
				</div>
				<ShopNav slug={slug} />
			</div>
		);
	}

	if (!data) {
		return (
			<div className="shop has-nav">
				<div className="shop-result"><span className="shop-spinner" /></div>
			</div>
		);
	}

	const fixture = data.fixture;
	const onSale = data.saleStatus === "onsale";

	const add = (pkg: any) => {
		Basket.add(slug, {
			name: pkg.name,
			occasion: `${fixture.isHome ? "v" : "away to"} ${fixture.opponent || "TBC"}`,
			pricePence: pkg.pricePence,
			productId: Number(id),
			productType: ProductType.Hospitality,
			quantity: 1,
			// The package rides in the ticket type slot, matching how the
			// server resolves it.
			ticketTypeId: pkg.packageId,
			when: fixture.kickOff
		});
		navigate(`/club/${slug}/basket`);
	};

	return (
		<div className="shop has-nav" style={clubTheme(data.club)}>
			<ShopHeader club={data.club} slug={slug} />
			<div className="shop-inner">
				<div className="shop-hero">
					<div className="shop-hero-body">
						<div className="shop-teams">
							<div className="shop-team">
								{data.club?.badgeUrl && <img alt="" src={data.club.badgeUrl} />}
								<span>{data.club?.name}</span>
							</div>
							<span className="shop-vs">vs</span>
							<div className="shop-team">
								{fixture.opponentBadgeUrl && <img alt="" src={fixture.opponentBadgeUrl} />}
								<span>{fixture.opponent || "TBC"}</span>
							</div>
						</div>
						<div className="shop-hero-meta">{longDate(fixture.kickOff)}</div>
					</div>
				</div>

				<h2 className="shop-section-title">Hospitality</h2>

				{!onSale &&
					<div className="shop-error">Hospitality isn&rsquo;t on sale for this game.</div>}

				{data.packages.map((pkg: any) => (
					<div className="shop-package" key={pkg.packageId}>
						<div className="shop-package-head">
							<strong>{pkg.name}</strong>
							<span className="shop-price">{money(pkg.pricePence)}</span>
						</div>
						{pkg.description && <p className="shop-package-desc">{pkg.description}</p>}
						<div className="shop-package-meta">
							{pkg.arrivalTime && <span>Arrive from {clock(pkg.arrivalTime)}</span>}
							{/* Stated rather than assumed. Turning up to an upgrade
							    without a match ticket is a ruined afternoon. */}
							<span className={pkg.includesAdmission ? "shop-included" : "shop-not-included"}>
								{pkg.includesAdmission ? "Match ticket included" : "Match ticket not included"}
							</span>
						</div>
						<button className="shop-btn" disabled={!onSale} onClick={() => add(pkg)} type="button">
							{onSale ? `Add · ${money(pkg.pricePence)}` : "Not on sale"}
						</button>
					</div>
				))}

				<button className="shop-btn secondary" onClick={() => navigate(`/club/${slug}/fixture/${id}`)} type="button">
					Match tickets instead
				</button>
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}
